package com.trevorwiebe.timesheet.core.presentation.common

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

enum class DraftButtonStyle { DESTRUCTIVE, OUTLINED, FILLED }

/**
 * One of the draft actions at the bottom of an invoice or settlement. The shared app buttons pad
 * their labels too much for three across a phone, so these use tight padding and a single line,
 * and share one height.
 */
@Composable
fun RowScope.DraftActionButton(
    text: String,
    style: DraftButtonStyle,
    onClick: () -> Unit,
    loading: Boolean = false,
    enabled: Boolean = true,
) {
    val colors = MaterialTheme.colorScheme
    val accent = when (style) {
        DraftButtonStyle.DESTRUCTIVE -> colors.error
        DraftButtonStyle.OUTLINED, DraftButtonStyle.FILLED -> if (enabled) colors.tertiary else colors.tertiaryContainer
    }
    val modifier = Modifier.weight(1f).height(48.dp)
    val padding = PaddingValues(horizontal = 6.dp)
    val label: @Composable RowScope.() -> Unit = {
        if (loading) {
            CircularProgressIndicator(
                modifier = Modifier.size(20.dp),
                strokeWidth = 2.dp,
                color = if (style == DraftButtonStyle.FILLED) colors.onTertiary else accent,
            )
        } else {
            Text(
                text = text,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                fontSize = 14.sp,
                color = if (style == DraftButtonStyle.FILLED) colors.onTertiary else accent,
            )
        }
    }
    if (style == DraftButtonStyle.FILLED) {
        Button(
            onClick = onClick,
            enabled = enabled,
            modifier = modifier,
            shape = CircleShape,
            contentPadding = padding,
            colors = ButtonDefaults.buttonColors(
                containerColor = colors.tertiary,
                disabledContainerColor = colors.tertiaryContainer,
            ),
            content = label,
        )
    } else {
        OutlinedButton(
            onClick = onClick,
            enabled = enabled,
            modifier = modifier,
            shape = CircleShape,
            contentPadding = padding,
            border = BorderStroke(if (style == DraftButtonStyle.OUTLINED) 2.dp else 1.dp, accent),
            content = label,
        )
    }
}
